import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Search, Building2, Users, FolderOpen, Bot, Ban, CheckCircle, Plus, Loader2, CheckCircle2, XCircle, Settings2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useDebounce } from '@/hooks/use-debounce'
import { adminService, type AdminOrganization } from '@/services/admin'
import { organizationService } from '@/services/organization'
import { createOrganizationSchema, type CreateOrganizationInput } from '@/schemas/organization'
import type { AxiosError } from 'axios'

type StatusFilter = 'all' | 'active' | 'inactive'
type SortOption = 'name_asc' | 'name_desc' | 'created_desc' | 'created_asc'

interface DialogState<T> {
  isOpen: boolean
  isProcessing: boolean
  error: string | null
  context: T | null
}

function createInitialDialogState<T>(): DialogState<T> {
  return {
    isOpen: false,
    isProcessing: false,
    error: null,
    context: null,
  }
}

function OrganizationCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gray-700/50" />
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-gray-700/50 rounded w-1/2" />
          <div className="h-4 bg-gray-700/50 rounded w-3/4" />
          <div className="flex gap-2">
            <div className="h-5 w-16 bg-gray-700/50 rounded-full" />
          </div>
        </div>
      </div>
    </Card>
  )
}

function EmptyState({ hasFilters, t }: { hasFilters: boolean; t: (key: string) => string }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <Building2 className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {hasFilters ? t('organizations.noResults') : t('organizations.noOrganizations')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {hasFilters ? t('organizations.noResultsDesc') : t('organizations.noOrganizationsDesc')}
      </p>
    </div>
  )
}

interface OrganizationCardProps {
  organization: AdminOrganization
  onToggleActive: (org: AdminOrganization) => void
  onDelete: (org: AdminOrganization) => void
  onManage: (org: AdminOrganization) => void
}

function OrganizationCard({ organization, onToggleActive, onDelete, onManage }: OrganizationCardProps) {
  const { t } = useTranslation('admin')

  return (
    <Card variant="glass" className="p-6 hover:bg-white/10 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-100 truncate">
                {organization.name}
              </h3>
              <p className="text-sm text-gray-400 font-mono truncate mt-1">
                @{organization.slug}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {organization.is_active ? (
              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                {t('organizations.active')}
              </span>
            ) : (
              <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                {t('organizations.inactive')}
              </span>
            )}
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Users className="w-4 h-4" />
              <span>{t('organizations.owner')}: {organization.owner?.full_name || organization.owner?.email || t('organizations.noOwner')}</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div className="flex items-center gap-1 text-gray-500">
                <Users className="w-4 h-4" />
                <span>{organization.members_count} {t('organizations.members')}</span>
              </div>
              <div className="flex items-center gap-1 text-gray-500">
                <FolderOpen className="w-4 h-4" />
                <span>{organization.projects_count} {t('organizations.projects')}</span>
              </div>
              <div className="flex items-center gap-1 text-gray-500">
                <Bot className="w-4 h-4" />
                <span>{organization.agents_count} {t('organizations.agents')}</span>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {t('organizations.created')}: {new Date(organization.created_at).toLocaleDateString()}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              size="sm"
              variant="action-purple"
              onClick={() => onManage(organization)}
              className="gap-2"
            >
              <Settings2 className="w-4 h-4" />
              {t('organizations.manageMembers')}
            </Button>
            <Button
              size="sm"
              variant={organization.is_active ? 'outline' : 'success'}
              onClick={() => onToggleActive(organization)}
              className="gap-2"
            >
              {organization.is_active ? (
                <>
                  <Ban className="w-4 h-4" />
                  {t('organizations.deactivate')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {t('organizations.activate')}
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDelete(organization)}
            >
              {t('organizations.delete')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

// Admin Create Organization Dialog
type SlugStatus = 'idle' | 'checking' | 'available' | 'unavailable'

interface AdminCreateOrgDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (org: AdminOrganization) => void
}

function AdminCreateOrgDialog({ open, onOpenChange, onCreated }: AdminCreateOrgDialogProps) {
  const { t } = useTranslation('admin')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle')
  const [slugSuggestion, setSlugSuggestion] = useState<string | null>(null)
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateOrganizationInput>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: { name: '', slug: '', description: '' },
  })

  const nameValue = watch('name')
  const slugValue = watch('slug')
  const debouncedName = useDebounce(nameValue, 400)
  const debouncedSlug = useDebounce(slugValue, 400)

  useEffect(() => {
    if (!debouncedName || isSlugManuallyEdited) return
    let cancelled = false
    organizationService
      .generateSlug(debouncedName)
      .then(({ slug }) => {
        if (!cancelled) setValue('slug', slug, { shouldValidate: true })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [debouncedName, isSlugManuallyEdited, setValue])

  const checkSlugAvailability = useCallback(async (slug: string, signal: AbortSignal) => {
    if (!slug || slug.length < 2) {
      if (!signal.aborted) setSlugStatus('idle')
      return
    }
    if (signal.aborted) return
    setSlugStatus('checking')
    setSlugSuggestion(null)
    try {
      const result = await organizationService.checkSlug(slug, { signal })
      if (signal.aborted) return
      setSlugStatus(result.available ? 'available' : 'unavailable')
      if (!result.available && result.suggestion) setSlugSuggestion(result.suggestion)
    } catch {
      if (!signal.aborted) setSlugStatus('idle')
    }
  }, [])

  useEffect(() => {
    if (!debouncedSlug) { setSlugStatus('idle'); return }
    const controller = new AbortController()
    checkSlugAvailability(debouncedSlug, controller.signal)
    return () => controller.abort()
  }, [debouncedSlug, checkSlugAvailability])

  const handleClose = (isOpen: boolean) => {
    if (!isSubmitting) {
      if (!isOpen) {
        reset()
        setIsSlugManuallyEdited(false)
        setSlugStatus('idle')
        setSlugSuggestion(null)
        setSubmitError(null)
      }
      onOpenChange(isOpen)
    }
  }

  const onSubmit = async (data: CreateOrganizationInput) => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const response = await adminService.createOrganization({
        name: data.name,
        slug: data.slug,
        description: data.description || undefined,
      })
      onCreated(response.organization)
      handleClose(false)
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setSubmitError(axiosError.response?.data?.detail || t('organizations.createError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSlugManuallyEdited(true)
    setValue('slug', e.target.value, { shouldValidate: true })
  }

  const renderSlugIndicator = () => {
    if (slugStatus === 'checking') {
      return (
        <span className="flex items-center gap-1 text-xs text-gray-400">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('organizations.create.slugChecking')}
        </span>
      )
    }
    if (slugStatus === 'available') {
      return (
        <span className="flex items-center gap-1 text-xs text-emerald-400">
          <CheckCircle2 className="w-3 h-3" />
          {t('organizations.create.slugAvailable')}
        </span>
      )
    }
    if (slugStatus === 'unavailable') {
      return (
        <span className="flex items-center gap-1 text-xs text-red-400">
          <XCircle className="w-3 h-3" />
          {t('organizations.create.slugUnavailable')}
          {slugSuggestion && (
            <button
              type="button"
              className="ml-1 underline"
              onClick={() => {
                setValue('slug', slugSuggestion, { shouldValidate: true })
                setIsSlugManuallyEdited(true)
              }}
            >
              {t('organizations.create.slugSuggestion', { slug: slugSuggestion })}
            </button>
          )}
        </span>
      )
    }
    return <span className="text-xs text-gray-500">{t('organizations.create.slugHint')}</span>
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700/50">
        <DialogHeader>
          <DialogTitle className="text-gray-100">{t('organizations.create.title')}</DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('organizations.create.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="admin-org-name" className="text-gray-300">
              {t('organizations.create.nameLabel')} <span className="text-red-400">*</span>
            </Label>
            <Input
              id="admin-org-name"
              {...register('name')}
              placeholder={t('organizations.create.namePlaceholder')}
              className="bg-white/5 border-gray-700/50 focus:border-purple-500/50"
              disabled={isSubmitting}
            />
            {errors.name && <p className="text-xs text-red-400">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-org-slug" className="text-gray-300">
              {t('organizations.create.slugLabel')} <span className="text-red-400">*</span>
            </Label>
            <Input
              id="admin-org-slug"
              value={slugValue}
              onChange={handleSlugChange}
              placeholder={t('organizations.create.slugPlaceholder')}
              className="bg-white/5 border-gray-700/50 focus:border-purple-500/50 font-mono"
              disabled={isSubmitting}
            />
            <div className="min-h-[16px]">{renderSlugIndicator()}</div>
            {errors.slug && <p className="text-xs text-red-400">{errors.slug.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-org-description" className="text-gray-300">
              {t('organizations.create.descriptionLabel')}
            </Label>
            <textarea
              id="admin-org-description"
              {...register('description')}
              placeholder={t('organizations.create.descriptionPlaceholder')}
              rows={3}
              disabled={isSubmitting}
              className="w-full rounded-md border border-gray-700/50 bg-white/5 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-500/50 focus:outline-none focus:ring-0 resize-none disabled:opacity-50"
            />
            {errors.description && <p className="text-xs text-red-400">{errors.description.message}</p>}
          </div>

          {submitError && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {submitError}
            </p>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={isSubmitting}
              className="border-gray-700/50"
            >
              {t('organizations.create.cancel')}
            </Button>
            <Button
              type="submit"
              variant="action-purple"
              disabled={isSubmitting || slugStatus === 'unavailable'}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('organizations.create.submitting')}
                </>
              ) : (
                t('organizations.create.submit')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function AdminOrganizationsPage() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sortOption, setSortOption] = useState<SortOption>('created_desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [toggleActiveDialog, setToggleActiveDialog] = useState<DialogState<AdminOrganization>>(
    createInitialDialogState()
  )
  const [deleteDialog, setDeleteDialog] = useState<DialogState<AdminOrganization>>(
    createInitialDialogState()
  )

  const debouncedSearch = useDebounce(searchTerm, 300)

  const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: t('organizations.filters.all') },
    { value: 'active', label: t('organizations.filters.active') },
    { value: 'inactive', label: t('organizations.filters.inactive') },
  ]

  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'name_asc', label: t('organizations.sort.nameAsc') },
    { value: 'name_desc', label: t('organizations.sort.nameDesc') },
    { value: 'created_desc', label: t('organizations.sort.createdDesc') },
    { value: 'created_asc', label: t('organizations.sort.createdAsc') },
  ]

  useEffect(() => {
    const controller = new AbortController()

    async function fetchOrganizations() {
      setIsLoading(true)
      try {
        const response = await adminService.listOrganizations(
          {
            search: debouncedSearch || undefined,
            status: statusFilter,
            page: currentPage,
            page_size: 12,
          },
          { signal: controller.signal }
        )
        setOrganizations(response.organizations)
        setTotalPages(response.total_pages)
      } catch (error) {
        if ((error as Error).name !== 'CanceledError') {
          console.error('Failed to fetch organizations:', error)
          setOrganizations([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchOrganizations()

    return () => {
      controller.abort()
    }
  }, [debouncedSearch, statusFilter, currentPage])

  const sortedOrganizations = useMemo(() => {
    return [...organizations].sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.name.localeCompare(b.name)
        case 'name_desc':
          return b.name.localeCompare(a.name)
        case 'created_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'created_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        default:
          return 0
      }
    })
  }, [organizations, sortOption])

  const hasFilters = searchTerm !== '' || statusFilter !== 'all'

  const handleOrganizationCreated = (org: AdminOrganization) => {
    setOrganizations((prev) => [org, ...prev])
  }

  const handleToggleActive = (organization: AdminOrganization) => {
    setToggleActiveDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: organization,
    })
  }

  const handleConfirmToggleActive = async () => {
    if (!toggleActiveDialog.context) return

    setToggleActiveDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await adminService.updateOrganization(toggleActiveDialog.context.id, {
        is_active: !toggleActiveDialog.context.is_active,
      })

      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === toggleActiveDialog.context?.id
            ? { ...org, is_active: !org.is_active }
            : org
        )
      )
      setToggleActiveDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setToggleActiveDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('organizations.updateError'),
      }))
    }
  }

  const handleDelete = (organization: AdminOrganization) => {
    setDeleteDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: organization,
    })
  }

  const handleConfirmDelete = async () => {
    if (!deleteDialog.context) return

    setDeleteDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await adminService.deleteOrganization(deleteDialog.context.id)
      setOrganizations((prev) => prev.filter((org) => org.id !== deleteDialog.context?.id))
      setDeleteDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setDeleteDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('organizations.deleteError'),
      }))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">{t('organizations.title')}</h1>
            <p className="text-gray-400 text-sm">{t('organizations.subtitle')}</p>
          </div>
        </div>
        <Button
          variant="action-purple"
          className="gap-2"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">{t('organizations.create.button')}</span>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder={t('organizations.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-gray-700/50 focus:border-purple-500/50"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {STATUS_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="focus:bg-white/5"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sortOption}
          onValueChange={(value) => setSortOption(value as SortOption)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {SORT_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="focus:bg-white/5"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {isLoading ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <OrganizationCardSkeleton key={index} />
            ))}
          </>
        ) : sortedOrganizations.length === 0 ? (
          <EmptyState hasFilters={hasFilters} t={t} />
        ) : (
          sortedOrganizations.map((organization) => (
            <OrganizationCard
              key={organization.id}
              organization={organization}
              onToggleActive={handleToggleActive}
              onDelete={handleDelete}
              onManage={(org) => navigate(`/admin/organizations/${org.id}`)}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            {t('organizations.previous')}
          </Button>
          <span className="flex items-center px-4 text-gray-400">
            {t('organizations.page')} {currentPage} {t('organizations.of')} {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            {t('organizations.next')}
          </Button>
        </div>
      )}

      <ConfirmationDialog
        open={toggleActiveDialog.isOpen}
        onOpenChange={(open) =>
          !open && !toggleActiveDialog.isProcessing && setToggleActiveDialog(createInitialDialogState())
        }
        title={
          toggleActiveDialog.context?.is_active
            ? t('organizations.deactivateTitle')
            : t('organizations.activateTitle')
        }
        description={
          toggleActiveDialog.context?.is_active
            ? t('organizations.deactivateDesc')
            : t('organizations.activateDesc')
        }
        confirmLabel={t('organizations.confirm')}
        confirmingLabel={t('organizations.confirming')}
        confirmVariant={toggleActiveDialog.context?.is_active ? 'action-red' : 'action-emerald'}
        isProcessing={toggleActiveDialog.isProcessing}
        error={toggleActiveDialog.error}
        onConfirm={handleConfirmToggleActive}
      />

      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) =>
          !open && !deleteDialog.isProcessing && setDeleteDialog(createInitialDialogState())
        }
        title={t('organizations.deleteTitle')}
        description={t('organizations.deleteDesc')}
        confirmLabel={t('organizations.deleteConfirm')}
        confirmingLabel={t('organizations.deleting')}
        confirmVariant="action-red"
        isProcessing={deleteDialog.isProcessing}
        error={deleteDialog.error}
        onConfirm={handleConfirmDelete}
      />

      <AdminCreateOrgDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleOrganizationCreated}
      />
    </div>
  )
}
